import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MetricCard from '../MetricCard';
import ModernLoader from '../ModernLoader';
import AffiliateLeaderboard from '../affiliate/AffiliateLeaderboard';
import AffiliateMetrics from '../affiliate/AffiliateMetrics';
import ClientList from '../affiliate/ClientList';
import ReferralHistory from '../affiliate/ReferralHistory';
import MyLinkModal from '../affiliate/MyLinkModal';
import styles from '../../styles/Team.module.css';

const AffiliatePage = ({ data, isLoading, error, onRetry }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [isMyLinkOpen, setIsMyLinkOpen] = useState(false);
  const [affiliateData, setAffiliateData] = useState({
    affiliateId: '23082325',
    rank: 3,
    totalEarnings: 2847.50,
    monthlyEarnings: 847.50,
    totalReferrals: 23,
    activeReferrals: 18,
    basePercentage: 4,
    monthlyStipend: 100,
    biggestWin: {
      client: 'Sarah Johnson',
      amount: 450.00,
      date: '2024-01-15'
    },
          leaderboard: [
        { id: 1, name: 'Alex Thompson', earnings: 5240.75, referrals: 45, rank: 1 },
        { id: 2, name: 'Maria Garcia', earnings: 3890.25, referrals: 32, rank: 2 },
        { id: 3, name: 'Mitchell Kowalchick', earnings: 2847.50, referrals: 23, rank: 3 },
        { id: 4, name: 'David Chen', earnings: 2156.80, referrals: 19, rank: 4 },
        { id: 5, name: 'Lisa Wang', earnings: 1890.45, referrals: 16, rank: 5 },
        { id: 6, name: 'Mike Rodriguez', earnings: 1650.30, referrals: 14, rank: 6 },
        { id: 7, name: 'Emma Wilson', earnings: 1420.90, referrals: 12, rank: 7 },
        { id: 8, name: 'James Brown', earnings: 1180.60, referrals: 10, rank: 8 }
      ],
    clients: [
      {
        id: 1,
        name: 'Sarah Johnson',
        email: 'sarah.j@email.com',
        joinDate: '2024-01-15',
        totalSpent: 11250.00,
        affiliateEarnings: 450.00,
        status: 'active'
      },
      {
        id: 2,
        name: 'Michael Davis',
        email: 'michael.d@email.com',
        joinDate: '2024-01-10',
        totalSpent: 8750.00,
        affiliateEarnings: 350.00,
        status: 'active'
      },
      {
        id: 3,
        name: 'Jennifer Lee',
        email: 'jennifer.l@email.com',
        joinDate: '2024-01-05',
        totalSpent: 6250.00,
        affiliateEarnings: 250.00,
        status: 'active'
      },
      {
        id: 4,
        name: 'Robert Wilson',
        email: 'robert.w@email.com',
        joinDate: '2023-12-28',
        totalSpent: 4500.00,
        affiliateEarnings: 180.00,
        status: 'inactive'
      },
      {
        id: 5,
        name: 'Amanda Taylor',
        email: 'amanda.t@email.com',
        joinDate: '2023-12-20',
        totalSpent: 3800.00,
        affiliateEarnings: 152.00,
        status: 'active'
      }
    ],
    referralHistory: [
      {
        id: 1,
        clientName: 'Sarah Johnson',
        date: '2024-01-15',
        purchaseAmount: 11250.00,
        affiliatePercentage: 4,
        earnings: 450.00,
        status: 'completed'
      },
      {
        id: 2,
        clientName: 'Michael Davis',
        date: '2024-01-10',
        purchaseAmount: 8750.00,
        affiliatePercentage: 4,
        earnings: 350.00,
        status: 'completed'
      },
      {
        id: 3,
        clientName: 'Jennifer Lee',
        date: '2024-01-05',
        purchaseAmount: 6250.00,
        affiliatePercentage: 4,
        earnings: 250.00,
        status: 'completed'
      }
    ]
  });



  if (isLoading) {
    return <ModernLoader message="Loading affiliate data" size="default" />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <FontAwesomeIcon icon="exclamation-triangle" className={styles.errorIcon} />
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={onRetry} className={styles.retryBtn}>
          <FontAwesomeIcon icon="sync" /> Retry
        </button>
      </div>
    );
  }

  if (!affiliateData) {
    return (
      <div className={styles.errorContainer}>
        <FontAwesomeIcon icon="exclamation-triangle" className={styles.errorIcon} />
        <h3>No Affiliate Data Available</h3>
        <p>Please check your affiliate status or contact support.</p>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {/* Header with My Link Button */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          <FontAwesomeIcon icon="handshake" className={styles.pageIcon} />
          <h1>Affiliate Dashboard</h1>
        </div>
        <button 
          className={styles.myLinkBtn}
          onClick={() => setIsMyLinkOpen(true)}
        >
          <FontAwesomeIcon icon="link" />
          My Link
        </button>
      </div>

      {/* Metrics Grid */}
      <div className={styles.metricsGrid} data-symmetry="5">
        <MetricCard
          icon={<FontAwesomeIcon icon="trophy" />}
          title="Your Rank"
          value={affiliateData.rank}
          subtitle={`${affiliateData.rank === 1 ? '🥇' : affiliateData.rank === 2 ? '🥈' : affiliateData.rank === 3 ? '🥉' : 'Top 10'}`}
        />
        <MetricCard
          icon={<FontAwesomeIcon icon="dollar-sign" />}
          title="Total Earnings"
          value={affiliateData.totalEarnings}
          subtitle={`+$${affiliateData.monthlyEarnings.toLocaleString()} this month`}
        />
        <MetricCard
          icon={<FontAwesomeIcon icon="users" />}
          title="Total Referrals"
          value={affiliateData.totalReferrals}
          subtitle={`${affiliateData.activeReferrals} active`}
        />
        <MetricCard
          icon={<FontAwesomeIcon icon="percentage" />}
          title="Commission Rate"
          value={affiliateData.basePercentage}
          subtitle={`+$${affiliateData.monthlyStipend} monthly stipend`}
        />
        <MetricCard
          icon={<FontAwesomeIcon icon="star" />}
          title="Biggest Win Today"
          value={affiliateData.biggestWin.amount}
          subtitle={affiliateData.biggestWin.client}
        />
      </div>

      {/* Client List - Full Width */}
      <div className={styles.dashboardCard} style={{ marginBottom: '24px' }}>
        <div className={styles.cardHeader}>
          <h3>
            <FontAwesomeIcon icon="user-friends" />
            Your Clients
          </h3>
          <span className={styles.cardSubtitle}>Referral Performance</span>
        </div>
        <ClientList 
          clients={affiliateData.clients}
          onClientSelect={setSelectedClient}
        />
      </div>

      {/* Leaderboard - Half Width */}
      <div className={styles.dashboardCard}>
        <div className={styles.cardHeader}>
          <h3>
            <FontAwesomeIcon icon="trophy" />
            Leaderboard
          </h3>
          <span className={styles.cardSubtitle}>Top Affiliates This Month</span>
        </div>
        <AffiliateLeaderboard 
          leaderboard={affiliateData.leaderboard}
          currentUserId={affiliateData.affiliateId}
        />
      </div>

      {/* Referral History Modal */}
      {selectedClient && (
        <ReferralHistory
          client={selectedClient}
          history={affiliateData.referralHistory}
          onClose={() => setSelectedClient(null)}
        />
      )}

      {/* My Link Modal */}
      {isMyLinkOpen && (
        <MyLinkModal
          affiliateId={affiliateData.affiliateId}
          onClose={() => setIsMyLinkOpen(false)}
        />
      )}
    </div>
  );
};

export default AffiliatePage; 